import type { ScoQuestionAnswerPayload } from '@src/components/sco/MarkdownRenderer/ScoChatEmbedContext'

function escapeAttr(value: string): string {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

/**
 * Build the human comment body: prose summary + machine tag.
 * Machine tags are never shown to end users in chat (see stripScoAnswerMachineTags /
 * isStructuredAnswerComment). Stored for model history + server embeds patch.
 */
export function formatScoAnswerComment(payload: ScoQuestionAnswerPayload): string {
  const attrs: string[] = [
    `question-id="${escapeAttr(payload.questionId)}"`,
    `field-type="${escapeAttr(payload.fieldType)}"`,
  ]
  if (payload.optionIds?.length) {
    attrs.push(`option-ids="${escapeAttr(payload.optionIds.join(','))}"`)
  }
  if (payload.value != null && payload.value !== '') {
    attrs.push(`value="${escapeAttr(String(payload.value))}"`)
  }
  if (payload.currency) {
    attrs.push(`currency="${escapeAttr(payload.currency)}"`)
  }
  if (payload.freeText) {
    attrs.push(`free-text="${escapeAttr(payload.freeText)}"`)
  }
  if (payload.storedFileIds?.length) {
    attrs.push(`stored-file-ids="${escapeAttr(payload.storedFileIds.join(','))}"`)
  }
  if (payload.valueJson != null) {
    try {
      attrs.push(`value-json="${escapeAttr(JSON.stringify(payload.valueJson))}"`)
    } catch {
      // omit
    }
  }
  const tag = `[sco-answer ${attrs.join(' ')}]`
  const prose = (payload.proseSummary || '').trim()
  return prose ? `${prose}\n${tag}` : tag
}

/** True when this body is a structured interactive answer (not free-form chat). */
export function isStructuredAnswerComment(body: string | null | undefined): boolean {
  if (!body) {
    return false
  }
  return /\[sco-answer\b/i.test(body)
}

/**
 * Strip machine tags from user message display.
 * Quote-aware so value-json arrays (which contain `]`) do not truncate early.
 */
export function stripScoAnswerMachineTags(body: string | null | undefined): string {
  if (!body) {
    return ''
  }
  let out = ''
  let i = 0
  const lower = body.toLowerCase()
  while (i < body.length) {
    const start = lower.indexOf('[sco-answer', i)
    if (start < 0) {
      out += body.slice(i)
      break
    }
    out += body.slice(i, start)
    let inQuotes = false
    let escaped = false
    let end = -1
    for (let j = start + 1; j < body.length; j++) {
      const c = body[j]
      if (escaped) {
        escaped = false
        continue
      }
      if (inQuotes && c === '\\') {
        escaped = true
        continue
      }
      if (c === '"') {
        inQuotes = !inQuotes
        continue
      }
      if (c === ']' && !inQuotes) {
        end = j
        break
      }
    }
    // Drop the whole tag (or rest of string if unclosed)
    i = end >= 0 ? end + 1 : body.length
  }
  return out
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Show raw machine tags when explicitly debugging (`?showScoAnswerTags=1`),
 * or for admins *if* the answer bubble is forced visible (same flag).
 * Never auto-on for NODE_ENV=development (that leaked tags to every local user).
 *
 * Note: answer bubbles are hidden for everyone including admins by default
 * (see ChatMessage `isHiddenStructuredAnswer`); this flag only affects tag
 * visibility when a bubble is shown.
 */
export function shouldShowScoAnswerMachineTags(opts: { isAdmin?: boolean; forceShow?: boolean }): boolean {
  return !!(opts.forceShow || opts.isAdmin)
}
